/**
 * Agent Service
 *
 * This service manages the enhanced agent system, including agent creation,
 * configuration, and communication with the CustomAgentProcessor in SpechopsSystemAgentRedux.
 *
 * The agent service provides:
 * 1. Functions for creating and managing agents
 * 2. Functions for creating agent actions and messages
 * 3. Functions for creating commands to send to the CustomAgentProcessor
 * 4. A hook (useAgentManager) for components to access agent functionality
 *
 * This service acts as the bridge between the UI components and the backend processor.
 */

import { useReactFlow, Node } from '@xyflow/react';
import { predefinedAgentRoles, getAgentRoleById } from '@/constants/agentRoles';
import {
  Agent,
  AgentRole,
  AgentAction,
  AgentMessage,
  CreateAgentProfileCommand,
  AgentActionCommand,
  AgentMonitorCommand,
  AgencyNodeData
} from '@/types/agentTypes';
import { nodeTypeNames } from '@/types/nodeTypes';

/**
 * Generate a unique ID for an agent
 *
 * This function generates a unique ID for an agent using a timestamp and a random string.
 * The format is: agent-[timestamp]-[random string]
 *
 * @returns A unique agent ID
 */
export const generateAgentId = (): string => {
  return `agent-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Create a new agent with default settings based on a role
 *
 * This function creates a new agent with default settings based on the specified role.
 * It retrieves the role information, sets up default properties, and returns a new Agent object.
 *
 * @param roleName The ID of the role to base the agent on
 * @param customName Optional custom name for the agent (defaults to role name)
 * @param customDescription Optional custom description for the agent (defaults to role description)
 * @returns A new Agent object
 * @throws Error if the specified role is not found
 */
export const createAgent = (
  roleName: string,
  customName?: string,
  customDescription?: string
): Agent => {
  // Special handling for custom role to prevent errors
  let role;
  
  if (roleName === 'custom') {
    // Fallback values if role is not found
    role = {
      id: 'custom',
      name: 'Custom Agent',
      description: 'A fully customizable agent with user-defined capabilities.',
      capabilities: ['userDefined', 'customizable', 'adaptable'],
      defaultNodeTypes: [],
      avatarOptions: ['/images/avatars/custom-agent-1.png']
    };
    console.log("Using fallback custom role definition");
  } else {
    // Try to get the role by ID
    role = getAgentRoleById(roleName);
    
    // If role not found, try to normalize the role ID
    if (!role) {
      // Try to normalize the role ID by removing hyphens and converting to camelCase
      const normalizedRoleId = roleName.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
      console.log(`Attempting to find role with normalized ID: ${normalizedRoleId}`);
      role = getAgentRoleById(normalizedRoleId);
      
      // Specific handling for known mismatches
      if (!role && roleName === 'data-analyst') {
        console.log("Correcting 'data-analyst' to 'dataAnalyst'");
        role = getAgentRoleById('dataAnalyst');
      }
    }
  }
  
  if (!role) {
    console.error(`Agent role "${roleName}" not found`);
    throw new Error(`Agent role "${roleName}" not found`);
  }
  
  return {
    id: generateAgentId(),
    name: customName || role.name,
    description: customDescription || role.description,
    role: roleName,
    isActive: true,
    isEnabled: true,
    isCustom: true,
    avatarImage: role.avatarOptions[0], // Default to first avatar
    monitoredNodes: {
      nodeTypes: role.defaultNodeTypes || [],
      monitorAll: false,
      nodeIds: [] // Initialize nodeIds as an empty array to prevent undefined errors
    },
    createdNodeTypes: role.defaultNodeTypes || [],
    actions: [], // No actions by default
    lastActivity: Date.now(),
    messages: [],
    settings: {
      notifyOnChanges: true,
      autoExecuteActions: false,
      priorityLevel: 'medium',
      triggerThreshold: 'significant',
      triggerDelay: 5, // 5 minutes
      checkSchedule: 'none'
    }
  };
};

/**
 * Create a default action for an agent
 *
 * This function creates a default action with the specified name, description, prompt, and trigger type.
 * It generates a unique ID for the action and sets up default result handling options.
 *
 * @param name The name of the action
 * @param description The description of what the action does
 * @param prompt The prompt template to use for the action
 * @param triggerType Whether the action is triggered manually or automatically (default: 'manual')
 * @returns A new AgentAction object
 */
export const createDefaultAction = (
  name: string,
  description: string,
  prompt: string,
  triggerType: 'manual' | 'automatic' = 'manual'
): AgentAction => {
  return {
    id: `action-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    name,
    description,
    triggerType,
    prompt,
    resultHandling: {
      updateSourceNode: true,
      notifyUser: true
    }
  };
};

/**
 * Create a message for an agent
 *
 * This function creates a message for an agent with the specified content, sender information,
 * and optional suggested responses. It generates a unique ID for the message and sets the timestamp.
 *
 * @param content The text content of the message
 * @param isAgentResponse Whether the message is from the agent (true) or the user (false)
 * @param email The email of the sender
 * @param suggestedResponses Optional suggested responses for the user to choose from
 * @returns A new AgentMessage object
 */
export const createAgentMessage = (
  content: string,
  isAgentResponse: boolean,
  email: string,
  suggestedResponses?: { id: string; text: string; action?: string; data?: any }[]
): AgentMessage => {
  return {
    id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    content,
    email,
    isAgentResponse,
    timestamp: Date.now(),
    suggestedResponses
  };
};

/**
 * Create a command to generate an agent profile using AI
 *
 * This function creates a command to send to the CustomAgentProcessor to generate
 * an agent profile using AI. The command includes the role and optional customizations.
 *
 * @param role The ID of the role to base the profile on
 * @param customizations Optional customizations for the profile (title, focus, expertise)
 * @returns A CreateAgentProfileCommand object
 */
export const createAgentProfileCommand = (
  role: string,
  customizations?: {
    title?: string;
    focus?: string[];
    expertise?: string[];
  }
): CreateAgentProfileCommand => {
  return {
    'sysagent-cmd': 'CustomAgent-Create-Started',
    'sysagent-response': '',
    timestamp: Date.now(),
    processing: false,
    role,
    customizations
  };
};

/**
 * Create a command to execute an agent action
 *
 * This function creates a command to send to the CustomAgentProcessor to execute
 * an agent action. The command includes the agent ID, action ID, target node IDs,
 * and an optional custom prompt.
 *
 * @param agentId The ID of the agent executing the action
 * @param actionId The ID of the action to execute
 * @param nodeIds The IDs of the nodes to process
 * @param customPrompt Optional custom prompt to use for the action
 * @returns An AgentActionCommand object
 */
export const createAgentActionCommand = (
  agentId: string,
  actionId: string,
  nodeIds: string[],
  customPrompt?: string
): AgentActionCommand => {
  return {
    'sysagent-cmd': 'CustomAgent-Action-Started',
    'sysagent-response': '',
    timestamp: Date.now(),
    processing: false,
    agentId,
    actionId,
    nodeIds,
    customPrompt
  };
};

/**
 * Create a command to configure agent monitoring
 *
 * This function creates a command to send to the CustomAgentProcessor to configure
 * agent monitoring. The command includes the agent ID and monitoring configuration.
 *
 * @param agentId The ID of the agent to configure monitoring for
 * @param monitorConfig The monitoring configuration
 * @returns An AgentMonitorCommand object
 */
export const createAgentMonitorCommand = (
  agentId: string,
  monitorConfig: Agent['monitoredNodes']
): AgentMonitorCommand => {
  return {
    'sysagent-cmd': 'CustomAgent-Monitor-Started',
    'sysagent-response': '',
    timestamp: Date.now(),
    processing: false,
    agentId,
    monitorConfig
  };
};

/**
 * Hook for managing agents in a node
 *
 * This hook provides functions for managing agents in a node, including:
 * - Getting the agency node data
 * - Adding, updating, and removing agents
 * - Adding messages to agents
 * - Configuring agent actions and monitoring
 *
 * It uses the ReactFlow hooks to access and update node data.
 *
 * @param nodeId The ID of the node to manage agents for (defaults to "Agency-1")
 * @returns An object with functions for managing agents
 */
export const useAgentManager = (nodeId: string) => {
  const { getNode, setNodes, updateNodeData } = useReactFlow();
  
  /**
   * Get the agency node data
   *
   * This function retrieves the agency node from ReactFlow.
   * The agency node always exists with ID "Agency-1".
   *
   * @returns The agency node or undefined if not found
   */
  const getAgencyNode = (): Node<AgencyNodeData> | undefined => {
    // Agency node always exists with id "Agency-1"
    return getNode(nodeId || "Agency-1");
  };
  
  /**
   * Add a new agent to the agency node
   *
   * This function adds a new agent to the agency node's data.
   * It retrieves the current agents, adds the new agent, and updates the node data.
   *
   * @param agent The agent to add
   * @returns The added agent or undefined if the agency node is not found
   */
  const addAgent = (agent: Agent) => {
    const node = getAgencyNode();
    if (!node) return;
    
    const agents: Agent[] = [...(node.data.agents || []), agent];
    updateNodeData(node.id, {
      ...node.data,
      agents
    });
    
    return agent;
  };
  
  /**
   * Update an existing agent
   *
   * This function updates an existing agent in the agency node's data.
   * It retrieves the current agents, updates the specified agent, and updates the node data.
   *
   * @param agentId The ID of the agent to update
   * @param updates The properties to update
   */
  const updateAgent = (agentId: string, updates: Partial<Agent>) => {
    const node = getAgencyNode();
    if (!node) return;
    
    const agents: Agent[] = node.data.agents || [];
    const updatedAgents = agents.map(agent => 
      agent.id === agentId ? { ...agent, ...updates } : agent
    );
    
    updateNodeData(node.id, {
      ...node.data,
      agents: updatedAgents
    });
  };
  
  /**
   * Remove an agent
   *
   * This function removes an agent from the agency node's data.
   * It retrieves the current agents, filters out the specified agent, and updates the node data.
   *
   * @param agentId The ID of the agent to remove
   */
  const removeAgent = (agentId: string) => {
    const node = getAgencyNode();
    if (!node) {
      return;
    }
    
    console.log(`Removing agent with ID: ${agentId}`);
    console.log(`Agent isCustom flag:`, node.data.agents.find(agent => agent.id === agentId)?.isCustom);
    console.log(`Current agents before removal:`, node.data.agents);
    
    const agents: Agent[] = node.data.agents || [];
    const updatedAgents = agents.filter(agent => agent.id !== agentId);
    
    console.log(`Updated agents after removal:`, updatedAgents);
    
    try {
      // Use the setNodes function that was already destructured from useReactFlow()
      // at the beginning of the useAgentManager hook
      setNodes(nodes => {
        return nodes.map(n => {
          if (n.id === node.id) {
            console.log(`Updating node ${n.id} to remove agent ${agentId}`);
            return {
              ...n,
              data: {
                ...n.data,
                agents: updatedAgents
              }
            };
          }
          return n;
        });
      });
      
      console.log("Node data updated using setNodes from useReactFlow()");
      
      // Verify the agent was removed
      setTimeout(() => {
        const updatedNode = getAgencyNode();
        if (updatedNode && updatedNode.data.agents) {
          const stillExists = updatedNode.data.agents.some(agent => agent.id === agentId);
          if (stillExists) {
            console.warn(`Agent ${agentId} still exists after removal attempt. Trying fallback method.`);
            // Try the fallback method if the agent still exists
            updateNodeData(updatedNode.id, {
              ...updatedNode.data,
              agents: updatedNode.data.agents.filter(agent => agent.id !== agentId)
            });
          } else {
            console.log(`Agent ${agentId} successfully removed.`);
          }
        }
      }, 100);
    } catch (updateError) {
      console.error("Error updating node data with setNodes:", updateError);
      
      // Fall back to updateNodeData
      console.log("Falling back to updateNodeData");
      updateNodeData(node.id, {
        ...node.data,
        agents: updatedAgents
      });
      
      // Verify the fallback worked
      setTimeout(() => {
        const updatedNode = getAgencyNode();
        if (updatedNode && updatedNode.data.agents) {
          const stillExists = updatedNode.data.agents.some(agent => agent.id === agentId);
          console.log(`After fallback, agent ${agentId} still exists: ${stillExists}`);
        }
      }, 100);
    }
  };
  
  /**
   * Add a message to an agent's chat
   *
   * This function adds a message to an agent's chat history.
   * It retrieves the current agents, adds the message to the specified agent,
   * updates the agent's last activity timestamp, and updates the node data.
   *
   * @param agentId The ID of the agent to add the message to
   * @param message The message to add
   */
  const addAgentMessage = (agentId: string, message: AgentMessage) => {
    const node = getAgencyNode();
    if (!node) return;
    
    const agents: Agent[] = node.data.agents || [];
    const updatedAgents = agents.map(agent => {
      if (agent.id === agentId) {
        const messages = [...(agent.messages || []), message];
        return {
          ...agent,
          messages,
          lastActivity: Date.now()
        };
      }
      return agent;
    });
    
    updateNodeData(node.id, {
      ...node.data,
      agents: updatedAgents
    });
  };
  
  /**
   * Add a message to Intellica (the lead agent)
   *
   * This function adds a message to the Intellica chat history.
   * It retrieves the current Intellica messages, adds the new message, and updates the node data.
   *
   * @param message The message to add
   */
  const addIntellicaMessage = (message: AgentMessage) => {
    const node = getAgencyNode();
    if (!node) return;
    
    const intellicaMessages: AgentMessage[] = [...(node.data.intellicaMessages || []), message];
    updateNodeData(node.id, {
      ...node.data,
      intellicaMessages
    });
  };
  
  /**
   * Enable or disable an agent
   *
   * This function toggles the enabled state of an agent.
   * It updates the agent with the new isEnabled value.
   *
   * @param agentId The ID of the agent to toggle
   * @param isEnabled Whether the agent should be enabled (true) or disabled (false)
   */
  const toggleAgentEnabled = (agentId: string, isEnabled: boolean) => {
    updateAgent(agentId, { isEnabled });
  };
  
  /**
   * Add an action to an agent
   *
   * This function adds an action to an agent.
   * It retrieves the current agents, adds the action to the specified agent,
   * updates the agent's last activity timestamp, and updates the node data.
   *
   * @param agentId The ID of the agent to add the action to
   * @param action The action to add
   */
  const addAgentAction = (agentId: string, action: AgentAction) => {
    const node = getAgencyNode();
    if (!node) return;
    
    const agents: Agent[] = node.data.agents || [];
    const updatedAgents = agents.map(agent => {
      if (agent.id === agentId) {
        const actions = [...(agent.actions || []), action];
        return {
          ...agent,
          actions,
          lastActivity: Date.now()
        };
      }
      return agent;
    });
    
    updateNodeData(node.id, {
      ...node.data,
      agents: updatedAgents
    });
  };
  
  /**
   * Update an agent's monitoring configuration
   *
   * This function updates an agent's monitoring configuration.
   * It updates the agent with the new monitoredNodes value.
   *
   * @param agentId The ID of the agent to update
   * @param monitorConfig The new monitoring configuration
   */
  const updateAgentMonitoring = (agentId: string, monitorConfig: Partial<Agent['monitoredNodes']>) => {
    const agent = getAgentById(agentId);
    if (agent) {
      // Merge the new config with existing config, ensuring we don't lose any properties
      const updatedMonitoredNodes = {
        nodeIds: Array.isArray(agent.monitoredNodes?.nodeIds) ? agent.monitoredNodes.nodeIds : [],
        nodeTypes: Array.isArray(agent.monitoredNodes?.nodeTypes) ? agent.monitoredNodes.nodeTypes : [],
        monitorAll: agent.monitoredNodes?.monitorAll || false,
        ...monitorConfig
      };
      updateAgent(agentId, { monitoredNodes: updatedMonitoredNodes });
    }
  };
  
  /**
   * Get all agents
   *
   * This function retrieves all agents from the agency node's data.
   *
   * @returns An array of all agents or an empty array if the agency node is not found
   */
  const getAgents = (): Agent[] => {
    const node = getAgencyNode();
    if (!node) return [];
    
    return node.data.agents || [];
  };
  
  /**
   * Get a specific agent by ID
   *
   * This function retrieves a specific agent by ID from the agency node's data.
   *
   * @param agentId The ID of the agent to retrieve
   * @returns The agent or undefined if not found
   */
  const getAgentById = (agentId: string) => {
    const agents = getAgents();
    return agents.find(agent => agent.id === agentId);
  };
  
  /**
   * Get all available agent roles
   *
   * This function retrieves all available agent roles from the predefined roles.
   *
   * @returns An array of all available agent roles
   */
  const getAvailableRoles = () => {
    return Object.values(predefinedAgentRoles);
  };
  
  return {
    getAgencyNode,
    addAgent,
    updateAgent,
    removeAgent,
    addAgentMessage,
    addIntellicaMessage,
    toggleAgentEnabled,
    addAgentAction,
    updateAgentMonitoring,
    getAgents,
    getAgentById,
    getAvailableRoles
  };
};

export default useAgentManager;